import { advertisementLeaveGuard } from './features/advertisements/pages/advertisements-form/advertisement-leave.guard';
import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { MasterPageComponent } from './core/components/master-page/master-page.component';
import { HomeIndexComponent } from './features/home/pages/home-index/home-index.component';
import { LoginComponent } from './features/home/authentication/pages/login/login.component';
import { ForbiddenComponent } from './features/home/authentication/pages/forbidden/forbidden.component';
import { NotEnabledComponent } from './features/home/authentication/pages/not-enabled/not-enabled.component';
import { canActivateAuthRole } from './shared/guards/keycloak-auth.guard';
import { unsavedChangesGuard } from './shared/guards/unsaved-changes.guard';
import { KEYCLOAK_ROLES } from './shared/constants/auth.constants';
import { sessionResolver } from './core/configs/session.resolver';

export const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'forbidden', component: ForbiddenComponent },
  { path: 'not-enabled', component: NotEnabledComponent },
  {
    path: '',
    component: MasterPageComponent,
    canActivateChild: [canActivateAuthRole],
    resolve: { session: sessionResolver },
    children: [
      {
        path: 'home',
        component: HomeIndexComponent,
      },
      {
        // Perfil do próprio usuário (foto). Self-service: sem role — qualquer autenticado acessa.
        path: 'usuario',
        loadComponent: () => import('./features/profile/pages/profile/profile.component').then(m => m.ProfileComponent)
      },
      {
        // Fila de pendências: agrega pedido/nota/pagamento/anúncio/integração. As roles são
        // "qualquer uma das" (o guard usa `some`) — quem só vê notas entra e recebe só notas,
        // porque o recorte por permissão é feito no backend.
        path: 'pendencias',
        data: {
          roles: [
            KEYCLOAK_ROLES.ORDER,
            KEYCLOAK_ROLES.INVOICE,
            KEYCLOAK_ROLES.ADVERTISEMENT,
            KEYCLOAK_ROLES.INTEGRATION,
          ],
        },
        loadComponent: () => import('./features/pendings/pages/pendings-list/pendings-list.component').then(m => m.PendingsListComponent)
      },
      {
        path: 'integracoes',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.INTEGRATION] },
            loadComponent: () => import('./features/integrations/pages/integrations-list/integrations-list.component').then(m => m.IntegrationsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.INTEGRATION] },
            loadComponent: () => import('./features/integrations/pages/integrations-form/integrations-form.component').then(m => m.IntegrationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.INTEGRATION] },
            loadComponent: () => import('./features/integrations/pages/integrations-form/integrations-form.component').then(m => m.IntegrationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'callback',
            data: { roles: [KEYCLOAK_ROLES.INTEGRATION] },
            loadComponent: () => import('./features/integrations/pages/integrations-callback/integrations-callback.component').then(m => m.IntegrationsCallbackComponent)
          }
        ]
      },
      {
        path: 'produtos',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.PRODUCT] },
            loadComponent: () => import('./features/products/pages/products-list/products-list.component').then(m => m.ProductsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.PRODUCT] },
            loadComponent: () => import('./features/products/pages/products-form/products-form.component').then(m => m.ProductsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.PRODUCT] },
            loadComponent: () => import('./features/products/pages/products-form/products-form.component').then(m => m.ProductsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },
      {
        path: 'categorias',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.CATEGORY] },
            loadComponent: () => import('./features/categories/pages/categories-list/categories-list.component').then(m => m.CategoriesListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.CATEGORY] },
            loadComponent: () => import('./features/categories/pages/categories-form/categories-form.component').then(m => m.CategoriesFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.CATEGORY] },
            loadComponent: () => import('./features/categories/pages/categories-form/categories-form.component').then(m => m.CategoriesFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },
      {
        path: 'variacoes',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.VARIATION] },
            loadComponent: () => import('./features/variations/pages/variations-list/variations-list.component').then(m => m.VariationsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.VARIATION] },
            loadComponent: () => import('./features/variations/pages/variations-form/variations-form.component').then(m => m.VariationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.VARIATION] },
            loadComponent: () => import('./features/variations/pages/variations-form/variations-form.component').then(m => m.VariationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },
      {
        path: 'anuncios',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            loadComponent: () => import('./features/advertisements/pages/advertisements-list/advertisements-list.component').then(m => m.AdvertisementsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            canDeactivate: [advertisementLeaveGuard],
            loadComponent: () => import('./features/advertisements/pages/advertisements-form/advertisements-form.component').then(m => m.AdvertisementsFormComponent)
          },
          {
            path: 'padrao',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            loadComponent: () => import('./features/advertisements/pages/advertisement-template/advertisement-template.component').then(m => m.AdvertisementTemplateComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'tabelas-de-medidas',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            loadComponent: () => import('./features/advertisements/pages/advertisement-size-charts/advertisement-size-charts.component').then(m => m.AdvertisementSizeChartsComponent)
          },
          {
            path: 'consultor',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            loadComponent: () => import('./features/advertisements/pages/advertisement-advisor/advertisement-advisor.component').then(m => m.AdvertisementAdvisorComponent)
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.ADVERTISEMENT] },
            canDeactivate: [advertisementLeaveGuard],
            loadComponent: () => import('./features/advertisements/pages/advertisements-form/advertisements-form.component').then(m => m.AdvertisementsFormComponent)
          }
        ]
      },
      {
        path: 'pedidos',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.ORDER] },
            loadComponent: () => import('./features/orders/pages/orders-list/orders-list.component').then(m => m.OrdersListComponent)
          },
          {
            path: ':id',
            data: { roles: [KEYCLOAK_ROLES.ORDER, KEYCLOAK_ROLES.INVOICE] },
            loadComponent: () => import('./features/orders/pages/order-detail/order-detail.component').then(m => m.OrderDetailComponent)
          }
        ]
      },
      {
        // Pagamentos virou parte dos Lotes de baixa (a baixa é sempre por NF): link antigo chega lá,
        // com a busca (?search=<pagamento>) que acha o lote do pagamento.
        path: 'pagamentos',
        redirectTo: ({ queryParams }) => inject(Router).createUrlTree(['/lotes-de-baixa'],
          { queryParams: typeof queryParams['search'] === 'string' && queryParams['search'] ? { search: queryParams['search'] } : {} }),
      },
      {
        // Lote de baixa por NF (repasse, tarifas, frete e os pagamentos de cada NF). Role de pedidos.
        path: 'lotes-de-baixa',
        data: { roles: [KEYCLOAK_ROLES.ORDER] },
        loadComponent: () => import('./features/settlement-lots/pages/settlement-lots-list/settlement-lots-list.component').then(m => m.SettlementLotsListComponent)
      },
      {
        path: 'notas-fiscais',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.INVOICE] },
            loadComponent: () => import('./features/invoices/pages/invoices-list/invoices-list.component').then(m => m.InvoicesListComponent)
          },
          {
            path: ':id',
            data: { roles: [KEYCLOAK_ROLES.INVOICE] },
            loadComponent: () => import('./features/invoices/pages/invoice-detail/invoice-detail.component').then(m => m.InvoiceDetailComponent)
          }
        ]
      },
      {
        path: 'conciliacao-financeira',
        data: { roles: [KEYCLOAK_ROLES.ORDER] },
        loadComponent: () => import('./features/financial-reconciliation/pages/financial-reconciliation-list/financial-reconciliation-list.component').then(m => m.FinancialReconciliationListComponent)
      },
      {
        path: 'conferencia',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.SALE_CONFERENCE] },
            loadComponent: () => import('./features/sale-conferences/pages/conference-list/conference-list.component').then(m => m.ConferenceListComponent)
          },
          {
            // Antes do ':id' — senão 'ranking' casa como id.
            path: 'ranking',
            // Permissão própria (perfil de acesso): quem só confere não vê o ranking.
            data: { roles: [KEYCLOAK_ROLES.SALE_CONFERENCE_RANKING] },
            loadComponent: () => import('./features/sale-conferences/pages/conference-ranking/conference-ranking.component').then(m => m.ConferenceRankingComponent)
          },
          {
            // Conferência pelo celular (câmera lê a nota e os itens). Também antes do ':id'.
            path: 'camera',
            data: { roles: [KEYCLOAK_ROLES.SALE_CONFERENCE] },
            loadComponent: () => import('./features/sale-conferences/pages/conference-camera/conference-camera.component').then(m => m.ConferenceCameraComponent)
          },
          {
            path: ':id',
            data: { roles: [KEYCLOAK_ROLES.SALE_CONFERENCE] },
            loadComponent: () => import('./features/sale-conferences/pages/conference-run/conference-run.component').then(m => m.ConferenceRunComponent)
          }
        ]
      },
      {
        path: 'destinos-webhook',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.WEBHOOK_DESTINATION] },
            loadComponent: () => import('./features/webhook-destinations/pages/webhook-destinations-list/webhook-destinations-list.component').then(m => m.WebhookDestinationsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.WEBHOOK_DESTINATION] },
            loadComponent: () => import('./features/webhook-destinations/pages/webhook-destinations-form/webhook-destinations-form.component').then(m => m.WebhookDestinationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.WEBHOOK_DESTINATION] },
            loadComponent: () => import('./features/webhook-destinations/pages/webhook-destinations-form/webhook-destinations-form.component').then(m => m.WebhookDestinationsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },
      {
        path: 'logs-processamento',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.PROCESSING_LOG] },
            loadComponent: () => import('./features/processing-logs/pages/processing-logs-list/processing-logs-list.component').then(m => m.ProcessingLogsListComponent)
          }
        ]
      },
      {
        path: 'grupos-empresas',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.COMPANY_GROUP] },
            loadComponent: () => import('./features/company-groups/pages/company-groups-list/company-groups-list.component').then(m => m.CompanyGroupsListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.COMPANY_GROUP] },
            loadComponent: () => import('./features/company-groups/pages/company-groups-form/company-groups-form.component').then(m => m.CompanyGroupsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.COMPANY_GROUP] },
            loadComponent: () => import('./features/company-groups/pages/company-groups-form/company-groups-form.component').then(m => m.CompanyGroupsFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      },
      {
        // Tela Acessos do admin do cliente: troca o perfil dos usuários do grupo da empresa ativa.
        path: 'acessos',
        data: { roles: [KEYCLOAK_ROLES.ACCESS_ADMIN] },
        loadComponent: () => import('./features/access-profiles/pages/user-access/user-access.component').then(m => m.UserAccessComponent)
      },
      {
        // Tela Identidade visual do admin do cliente: logo, cor e nome de exibição do grupo.
        path: 'identidade-visual',
        data: { roles: [KEYCLOAK_ROLES.BRAND_IDENTITY] },
        loadComponent: () => import('./features/brand-identity/pages/brand-identity/brand-identity.component').then(m => m.BrandIdentityComponent)
      },
      {
        path: 'empresas',
        children: [
          {
            path: '',
            data: { roles: [KEYCLOAK_ROLES.COMPANY] },
            loadComponent: () => import('./features/companies/pages/companies-list/companies-list.component').then(m => m.CompaniesListComponent)
          },
          {
            path: 'novo',
            data: { roles: [KEYCLOAK_ROLES.COMPANY] },
            loadComponent: () => import('./features/companies/pages/companies-form/companies-form.component').then(m => m.CompaniesFormComponent),
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: 'editar/:id',
            data: { roles: [KEYCLOAK_ROLES.COMPANY] },
            loadComponent: () => import('./features/companies/pages/companies-form/companies-form.component').then(m => m.CompaniesFormComponent),
            canDeactivate: [unsavedChangesGuard]
          }
        ]
      }
    ],
  },
  { path: '**', redirectTo: 'home' },
];
